/**
 * Scanbrix iOS app button / section click funnel (GA4 property 507368100).
 * Aggregate counts only — no Firebase UID required for the CRM view.
 */

import { IOS_GA4_PROPERTY_ID } from "./productAnalyticsEvents";

/** Ordered funnel steps for drop-off visualization. */
export const CLICK_FUNNEL_STEPS = [
  {
    key: "signup_login",
    label: "Signup / login",
    events: ["sign_up_completed", "login_completed", "login", "sign_up"],
    description: "Account created or returning user signed in",
    instrumented: true,
  },
  {
    key: "onboarding_details",
    label: "Onboarding details",
    events: ["onboarding_completed"],
    description: "Finished all onboarding screens (one count per completed flow, not per question)",
    instrumented: true,
  },
  {
    key: "start_scanning_onboarding",
    label: "Start scanning (onboarding)",
    events: ["post_onboarding_start_scanning_tapped"],
    description: "Tapped Start Scanning on the post-onboarding screen",
    instrumented: true,
  },
  {
    key: "watch_tutorial_onboarding",
    label: "Watch tutorial (onboarding)",
    events: ["post_onboarding_watch_tutorial_tapped"],
    description: "Tapped Watch Tutorial on the post-onboarding screen",
    instrumented: true,
  },
  {
    key: "practice_space",
    label: "Practice space workspace",
    events: ["practice_workspace_opened"],
    description: "Opened the Practice Space workspace",
    instrumented: true,
  },
  {
    key: "new_workspace",
    label: "New workspace",
    events: ["workspace_created"],
    description: "Created a new client or practice workspace",
    instrumented: true,
  },
  {
    key: "start_scanning",
    label: "Start scanning",
    events: ["scan_started"],
    description: "Started a scan session",
    instrumented: true,
  },
  {
    key: "upload",
    label: "Upload button",
    events: ["upload_button_tapped", "file_upload_started"],
    description: "Tapped upload during or after scanning",
    instrumented: false,
  },
  {
    key: "stop_scanning",
    label: "Stop scanning",
    events: ["scan_stopped"],
    description: "Stopped an in-progress scan",
    instrumented: false,
  },
  {
    key: "save_scan",
    label: "Save scan",
    events: ["scan_saved", "scan_finished", "scan_completed"],
    description: "Saved or finished a scan",
    instrumented: false,
  },
  {
    key: "order",
    label: "Order",
    events: ["order_started", "order_placed", "purchase"],
    description: "Started or placed an order",
    instrumented: true,
  },
];

const CLICK_EVENT_ALIASES = {
  sign_up_completed: "sign_up_completed",
  login_completed: "login_completed",
  login: "login",
  sign_up: "sign_up",
  onboarding_completed: "onboarding_completed",
  onboarding_step_completed: "onboarding_step_completed",
  onboarding_started: "onboarding_started",
  post_onboarding_start_scanning_tapped: "post_onboarding_start_scanning_tapped",
  post_onboarding_watch_tutorial_tapped: "post_onboarding_watch_tutorial_tapped",
  post_onboarding_scan_prompt_shown: "post_onboarding_scan_prompt_shown",
  practice_workspace_opened: "practice_workspace_opened",
  workspace_created: "workspace_created",
  scan_started: "scan_started",
  upload_button_tapped: "upload_button_tapped",
  file_upload_started: "file_upload_started",
  scan_stopped: "scan_stopped",
  scan_saved: "scan_saved",
  scan_finished: "scan_finished",
  scan_completed: "scan_completed",
  order_started: "order_started",
  order_placed: "order_placed",
  purchase: "purchase",
};

/** Related GA4 events shown in breakdown only — excluded from funnel step totals. */
export const CLICK_SUPPLEMENTAL_EVENTS = [
  {
    stepLabel: "Onboarding details",
    event: "onboarding_step_completed",
    note: "Per-question taps (not counted in funnel)",
  },
  {
    stepLabel: "Onboarding details",
    event: "onboarding_started",
    note: "Onboarding flow started",
  },
];

export const ALL_CLICK_GA4_EVENTS = [
  ...new Set([
    ...CLICK_FUNNEL_STEPS.flatMap((s) => s.events),
    "onboarding_step_completed",
    "onboarding_started",
  ]),
];

export { IOS_GA4_PROPERTY_ID };

export function normalizeClickEventName(raw) {
  const n = String(raw ?? "").trim().toLowerCase();
  return CLICK_EVENT_ALIASES[n] || n;
}
